import React, { useState } from 'react';
import { useAtom } from 'jotai';
import SignupForm, { SignupFormValues } from 'components/auth/components/SignupForm';
import { authIdentifierAtom, authIdentifierTypeAtom, authStepAtom } from 'auth/atoms';
import { useSignUp } from '@clerk/nextjs';
import PageHeader from 'components/auth/components/PageHeader';
import { FlexCol, FlexColAlignCenterBoth, FlexJustifyCenter } from 'components/primitives/flex';
import generatePassword from 'utils/random';
import Cookies from 'js-cookie';
import useLazySnackbar from 'components/banners/useLazySnackbar';
import useTrackComponentMount from 'hooks/useTrackComponentMount';
import dayjs from 'dayjs';
import LoadingSpinner from 'components/loading/LoadingSpinner';
import useRudderStackTrack from 'hooks/useRudderStackTrack';

const SignupDetailsStep = () => {
  const { enqueueSnackbar } = useLazySnackbar();
  const [identifier] = useAtom(authIdentifierAtom);
  const [identifierType] = useAtom(authIdentifierTypeAtom);
  const [, setStep] = useAtom(authStepAtom);
  const { signUp, isLoaded } = useSignUp();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const rudderTrack = useRudderStackTrack();

  useTrackComponentMount('show_form', {
    component_type: 'form',
    object: 'sign_up_complete_details_form',
  });

  const detailsDefaultValues: SignupFormValues = {
    first_name: '',
    last_name: '',
    email: identifierType === 'email' ? identifier : '',
    birthdate: '',
    cell_number: identifierType === 'phone' ? identifier : '',
    password: '',
    privacy_policy: false,
    sms_marketing: false,
  };

  const hiddenFields: string[] = [];
  if (identifierType === 'phone') {
    hiddenFields.push('email', 'password');
  } else {
    hiddenFields.push('cell_number', 'sms_marketing');
  }

  const disabledFields: string[] = [];
  if (identifierType === 'phone') {
    disabledFields.push('cell_number');
  } else {
    disabledFields.push('email');
  }

  const onDetailsSubmit = async (values: SignupFormValues) => {
    rudderTrack('click', {
      component_type: 'button',
      object: 'confirm',
      context: 'sign_up_complete_details_form',
    });

    if (!isLoaded) return;

    setIsSubmitting(true);

    // For details on unsafe metadata - https://clerk.com/docs/users/metadata#unsafe-metadata
    const now = new Date();
    const unsafeMetadata = {
      analytics_uuid: Cookies.get('uuid'),
      initial_anonymous_id: Cookies.get('initial_anonymous_id'),
      accepted_terms_at: values.privacy_policy ? now.toISOString() : null,
      tcpa_accepted_at: values.sms_marketing ? now.toISOString() : null,
      birthdate: values.birthdate ? dayjs(values.birthdate).format('YYYY-MM-DD') : null,
    };

    try {
      if (identifierType === 'phone') {
        await signUp.create({ ...values, password: generatePassword(), phoneNumber: identifier, unsafeMetadata });
        await signUp.preparePhoneNumberVerification({ strategy: 'phone_code' });
      } else {
        await signUp.create({ ...values, emailAddress: identifier, unsafeMetadata });
        await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
      }
      setStep('verification');
    } catch (err) {
      enqueueSnackbar(err.message, {
        variant: 'error',
        autoHideDuration: 5000,
      });
      console.error('Signup details submission error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitting) {
    return (
      <FlexColAlignCenterBoth minHeight="60vh">
        <LoadingSpinner size="large" color="#72D4BA" />
      </FlexColAlignCenterBoth>
    );
  }

  return (
    <FlexCol gap="20px">
      <PageHeader
        title="Complete your details"
        backAction={() => {
          rudderTrack('click', {
            component_type: 'button',
            object: 'back_to_start',
            context: 'sign_up_complete_details_form',
          });

          setStep('identifier');
        }}
      />
      <SignupForm
        defaultValues={detailsDefaultValues}
        ctaText="Confirm"
        processSubmit={onDetailsSubmit}
        processing={!isLoaded || isSubmitting}
        hiddenFields={hiddenFields}
        disabledFields={disabledFields}
        isSignup
      />
      <FlexJustifyCenter>
        <div id="clerk-captcha" data-cl-theme="light" data-cl-size="flexible" />
      </FlexJustifyCenter>
    </FlexCol>
  );
};

export default SignupDetailsStep;
